/**
 * normesService — wrapper rétro-compatible au-dessus de `clausierService`.
 *
 * Avant : DB IndexedDB séparée (RapteckNormes) + fetch `/referentiels/normes` +
 * fetch `referentielService.getReferentiel()` puis filtrage par norme_code, avec
 * un cache par norme et un mécanisme `preloadAllNormes()` qui pré-chargeait N normes.
 *
 * Maintenant : tout est dans l'arbre clausier (cache 24h + ETag, partagé).
 * Plus besoin de DB séparée ni de pré-chargement par norme : l'arbre contient déjà
 * tous les templates de toutes les normes. On expose simplement les helpers attendus
 * par `useNormesTemplates` :
 *   - getListeNormes()           → Array<NormeMeta>
 *   - getNormeTemplates(normeId) → { norme, templates }
 *   - preloadAllNormes()         → no-op (l'arbre est déjà tout-en-un)
 */

import clausierService, { extractNormesList, flattenNormeTemplates } from './clausierService';

class NormesService {
  /** Liste plate des normes (sans paragraphes/templates). */
  async getListeNormes() {
    const tree = await clausierService.getTree();
    return extractNormesList(tree);
  }

  /**
   * Récupère les templates d'une norme spécifique.
   * Format conservé : { norme: {...}, templates: [...] }.
   */
  async getNormeTemplates(normeId) {
    const tree = await clausierService.getTree();
    const norme = (tree.normes || []).find((n) => n.id === normeId);
    if (!norme) {
      throw new Error('Norme introuvable');
    }
    const allTemplates = flattenNormeTemplates({ normes: [norme] });
    return {
      norme: {
        id: norme.id,
        code: norme.code,
        titre: norme.titre,
        domaine: norme.domaine,
        annee: norme.annee,
      },
      templates: allTemplates,
    };
  }

  /**
   * Pré-chargement : maintenant un no-op car l'arbre clausier contient déjà tout.
   * On déclenche juste un fetch (avec ETag) pour s'assurer que l'arbre est en cache.
   */
  async preloadAllNormes() {
    try {
      await clausierService.getTree();
    } catch (err) {
      console.warn('⚠️ preloadAllNormes (clausier) :', err?.message || err);
    }
  }

  async clearCache() {
    await clausierService.clearCache();
  }

  async getCacheStats() {
    const stats = await clausierService.getCacheStats();
    if (!stats || !stats.exists) {
      return { liste_normes_cached: false, normes_cached: 0, cache_keys: [] };
    }
    return {
      liste_normes_cached: true,
      normes_cached: stats.normes || 0,
      cache_keys: [],
    };
  }

  /**
   * Recherche un template par ID dans l'arbre.
   * Conservé pour rétro-compatibilité (`findTemplateById`).
   */
  async findTemplateById(templateId) {
    const tree = await clausierService.getTree();
    for (const norme of tree.normes || []) {
      for (const para of norme.paragraphes || []) {
        for (const cat of para.categories || []) {
          for (const tpl of cat.templates || []) {
            if (tpl.id === templateId) {
              return {
                ...tpl,
                norme: {
                  id: norme.id,
                  code: norme.code,
                  titre: norme.titre,
                  domaine: norme.domaine,
                  annee: norme.annee,
                },
                paragraphe_code: para.code,
                paragraphe_titre: para.titre,
                categorie_code: cat.code,
                categorie_titre: cat.titre,
              };
            }
          }
        }
      }
    }
    return null;
  }
}

const normesServiceInstance = new NormesService();
export default normesServiceInstance;
